import type { Car } from "../physics/car";
import type { Track } from "../track/track";

export interface Minimap {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  // Pre-computed track path in minimap coordinates
  trackPath: { x: number; y: number }[];
  // Start/finish position in minimap coordinates
  sfPos: { x: number; y: number };
  // Transform parameters: world → minimap
  offsetX: number;
  offsetY: number;
  scale: number;
}

const SIZE = 160;
const PADDING = 12;

export function createMinimap(track: Track): Minimap {
  const canvas = document.getElementById("minimap") as HTMLCanvasElement;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = SIZE * dpr;
  canvas.height = SIZE * dpr;

  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);

  // Compute bounding box of track centerline
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  for (const p of track.centerline) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }

  const rangeX = maxX - minX;
  const rangeY = maxY - minY;
  const drawSize = SIZE - PADDING * 2;
  const scale = drawSize / Math.max(rangeX, rangeY);

  // Center the track in the minimap
  const offsetX = PADDING + (drawSize - rangeX * scale) / 2 - minX * scale;
  const offsetY = PADDING + (drawSize - rangeY * scale) / 2 - minY * scale;

  // Pre-compute track path in minimap coordinates
  const trackPath = track.centerline.map((p) => ({
    x: p.x * scale + offsetX,
    y: SIZE - (p.y * scale + offsetY), // flip Y for canvas
  }));

  const sfPt = track.centerline[track.startFinishIndex];
  const sfPos = {
    x: sfPt.x * scale + offsetX,
    y: SIZE - (sfPt.y * scale + offsetY),
  };

  return { canvas, ctx, trackPath, sfPos, offsetX, offsetY, scale };
}

export function renderMinimap(minimap: Minimap, car: Car): void {
  const { ctx, trackPath, sfPos, offsetX, offsetY, scale } = minimap;

  ctx.clearRect(0, 0, SIZE, SIZE);

  // Background
  ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
  ctx.beginPath();
  ctx.roundRect(0, 0, SIZE, SIZE, 8);
  ctx.fill();

  // Draw track outline
  ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(trackPath[0].x, trackPath[0].y);
  for (let i = 1; i < trackPath.length; i++) {
    ctx.lineTo(trackPath[i].x, trackPath[i].y);
  }
  ctx.closePath();
  ctx.stroke();

  // Start/finish marker
  ctx.fillStyle = "#fff";
  ctx.fillRect(sfPos.x - 3, sfPos.y - 3, 6, 6);

  // Car position
  const carX = car.x * scale + offsetX;
  const carY = SIZE - (car.y * scale + offsetY);

  // Direction indicator (line from car dot in heading direction)
  const dirLen = 8;
  const dirX = carX + Math.cos(-car.angle) * dirLen;
  const dirY = carY + Math.sin(-car.angle) * dirLen;

  ctx.strokeStyle = "#0f0";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(carX, carY);
  ctx.lineTo(dirX, dirY);
  ctx.stroke();

  // Car dot
  ctx.fillStyle = "#0f0";
  ctx.beginPath();
  ctx.arc(carX, carY, 4, 0, Math.PI * 2);
  ctx.fill();
}
